// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import {
  Credential,
  PartyCredentialRequirement,
} from '@daml.js/utility-credential-v0/lib/Utility/Credential/V0/Credential';
import TemplateContract from './TemplateContract';

/**
 * This function validates that a credential fulfills certain requirements. It is pure, i.e., it
 * always returns the same result given the same input and has no side effects.
 */
export function validateFulfillsRequirement(
  now: Date,
  expectedHolder: string,
  partyCredentialRequirement: PartyCredentialRequirement,
  credential: Credential,
): boolean {
  // Check if the issuer matches.
  if (credential.issuer !== partyCredentialRequirement.issuer) return false;
  // Check if holder is matching the expected holder.
  if (credential.holder !== expectedHolder) return false;
  // Check if the credential fulfills valid from.
  if (credential.validFrom && now < new Date(credential.validFrom)) return false;
  // Check if the credential fulfills valid until.
  if (credential.validUntil && now > new Date(credential.validUntil)) return false;
  // Check if all required claims are satisfied (with subject == holder).
  return partyCredentialRequirement.requiredClaims.every((requiredClaim) => {
    return credential.claims.some((claim) => {
      return (
        claim.subject === credential.holder &&
        claim.property === requiredClaim._1 &&
        claim.value === requiredClaim._2
      );
    });
  });
}

/**
 * This function validates that the credentials fulfill all requirements and returns an object
 * containing a boolean flag indicating if all requirements were met and the list of unique
 * credentials that fulfill each requirement.
 */
export function validateFulfillsAllRequirements(
  now: Date,
  expectedHolder: string,
  partyCredentialRequirements: PartyCredentialRequirement[],
  credentials: TemplateContract<Credential>[],
): { fulfillsAllRequirements: boolean; fulfillingCredentials: TemplateContract<Credential>[] } {
  const fulfillingCredentialsSet = new Set<TemplateContract<Credential>>();
  const fulfillsAllRequirements = partyCredentialRequirements.every((requirement) => {
    const matchingCredential = credentials.find((credential) =>
      validateFulfillsRequirement(now, expectedHolder, requirement, credential.payload),
    );
    if (matchingCredential) {
      fulfillingCredentialsSet.add(matchingCredential);
      return true;
    }
    return false;
  });
  return {
    fulfillsAllRequirements,
    fulfillingCredentials: Array.from(fulfillingCredentialsSet),
  };
}
